import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  QueryList,
  TemplateRef,
  ViewChildren
} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import {ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Searchable} from "../../interfaces/user";
import {SearchService} from "../../shared/search.service";
import {BSValidators} from "../../shared/bsvalidators";
import _ from 'lodash';
import {SearchResult} from "../../interfaces/searchResult";
import {
  NbComponentOrCustomStatus,
  NbComponentSize,
  NbFormControlState, NbLayoutScrollService, NbOptionComponent,
  NbScrollStrategy,
  NbScrollStrategyOptions
} from "@nebular/theme";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {Globals} from "../../shared/Globals";
import {StyledBaseComponent} from "../styled-base/styled-base.component";

@Component({
    selector: 'bst-list-adder',
    templateUrl: './list-adder.component.html',
    styleUrls: ['./list-adder.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ListAdderComponent),
            multi: true
        }
    ],
    animations: [
        trigger("added", [
            state("slideIn", style({
                maxWidth: '500px',
                whiteSpace: 'normal'
            })),
            state("slideTop", style({
                maxHeight: 'unset',
                whiteSpace: 'normal'
            })),
            transition("void => slideIn", [
                style({ maxWidth: '0', transform: "translateX(-100%)", whiteSpace: 'nowrap' }),
                animate("300ms ease-in-out")
            ]),
            transition("void => slideTop", [
                style({ maxHeight: '0', transform: "translateY(-100%)", whiteSpace: 'nowrap' }),
                animate("300ms ease-in-out")
            ])
        ])
    ],
    standalone: false
})
export class ListAdderComponent<T extends Searchable> extends StyledBaseComponent implements ControlValueAccessor, NbFormControlState{

  flag: boolean = true;
  private focusIndex: number = -1;
  @ViewChildren('element') elements: QueryList<NbOptionComponent>;

  public readonly mintyping: number = 2;

  public appending: boolean = false;

  @Input() public readonly scrollStrategy: NbScrollStrategy;
  @Input() public readonly title_key: string = 'name';
  // @Input() public maxlength: number = null;
  @Input() focused: boolean = false;
  // @Input() fullWidth: boolean = false;
  @Input() fullWidthResults: boolean = false;
  // @Input() size: NbComponentSize = 'medium';
  // @Input() status: NbComponentOrCustomStatus = 'basic';
  @Input() single: boolean = false;
  @Input() addnew: (event:any)=>Promise<T> = null;
  @Input() saveunknown: boolean = false;
  @Input() vertical:boolean = false;
  @Input() logos: boolean = false;
  @Input() noresults: string = 'noresults';
  @Input() filters: {[key: string]: any} = {
    status: 1
  };
  // @Input() placeholder: string = null;

  // @Input() public disabled: boolean = false;
  @Input() public target: string;
  @Input() public options: object = null;
  @Input() public selections: T[] = [];
  @Input() public selected: T = null;
  @Input() public autolist: boolean = false;
  /** Optional client-side re-sort of search results (e.g. by match score). */
  @Input() public sortResults: (a: T, b: T) => number = null;

  @Input() public listTemplate: TemplateRef<any>;
  @Input() public selectionTemplate: TemplateRef<any>;

  public readonly removeItem = (sel: T) => this.delete(sel);

  // Function to call when the value changes.
  onChange = (value: T[]|T) => {};
  // Function to call when the input is touched (when a star is clicked).
  onTouched = () => {};

  private res: T[] = [];
  private page: number = 1;

  public readonly keysearch;
  public adding: boolean = false;
  public form: FormGroup;

  constructor(private searchService: SearchService,
              private scrollService: NbLayoutScrollService,
              private readonly sso: NbScrollStrategyOptions,
              private detector: ChangeDetectorRef,
              private fb: FormBuilder) {
    super();
    // this.scrollStrategy = this.sso.block();
    this.scrollStrategy = this.sso.reposition();

    let a: Promise<T[]> = Promise.resolve(null);
    this.keysearch = _.debounce(() => {
      a = a.then((re) => {
        if(this.target) {
          return this.search().then(res => {
            this.form.get('search').setErrors((res && res.length) || (this.saveunknown && this.curlength)
              ? null : {incorrect: true});
            this.loading.next(false);
            return res;
          });
        }
        return null;
      });
      return a;
    }, 300);

    this.form = this.fb.group({
      search: [null, [BSValidators.required]]
    });
  }

  public get results(){
    return this.res;
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if(this.form && this.form.get('search')){
      const s = this.form.get('search');
      if(this.disabled){
        s.disable();
      }
      else{
        s.enable();
      }
    }
  }

  logo(res: any){
    if(res.logo && res.logo.preview){
      return Globals.storage(res.logo.preview)
    }
    return null;
  }

  writeValue(value: T[]|T): void {
    // CVA contract: writeValue is model -> view only. It must NOT call
    // this.onChange (view -> model), or a programmatic
    // control.setValue(v, { emitEvent: false }) still re-fires valueChanges
    // because the value gets pushed back out through onChange (#558). Update
    // internal display state only; user-driven changes propagate via add()/
    // remove(), which call onChange themselves.
    if(!this.single && value instanceof Array) {
      this.selections = value ? [...value] : [];
    }
    else if(this.single && !(value instanceof Array)){
      this.selected = value;
    }
    else if(!value){
      this.selections = [];
      this.selected = null;
    }
  }

  private isSelected(o: T) {
    if(this.single){
      return o === this.selected;
    }

    return this.selections.findIndex((m) => {
      return m.id === o.id
    }) !== -1;
  }

  get textval(){
    return this.form.get('search').value;
  }

  async search() {
    const val = this.textval;
    if(this.focused && ((val && val.length >= this.mintyping) || this.autolist)) {
      const res = <SearchResult<T>>(await this.searchService.search(this.target, {...this.form.getRawValue(), ...this.filters}, null, this.page));
      this.res = res.results.filter((o) => {
        return !this.isSelected(o);
      });
      if (this.sortResults) {
        this.res = [...this.res].sort(this.sortResults);
      }

      this.detector.detectChanges();
      this.scrollService.fireScrollChange(null);
    }
    else{
      this.res = [];
    }
    return this.res;
  }

  keydown($event: KeyboardEvent){
    if(this.single && this.selected){
      $event.preventDefault();
      $event.stopPropagation();
      if($event.key === 'Delete' || $event.key === 'Backspace'){
        return this.delete(this.selected);
      }
    }
    else if(($event.key === 'ArrowDown' || $event.key === 'ArrowUp') && this.elements && this.elements.length) {
      $event.preventDefault();
      $event.stopPropagation();
      this.elements.forEach(o => o.deselect());

      if ($event.key === 'ArrowDown') {
        this.focusIndex += 1;
        if(this.focusIndex >= this.elements.length){
          this.focusIndex = 0;
        }
      } else if ($event.key === 'ArrowUp') {
        this.focusIndex -= 1;
        if(this.focusIndex < 0){
          this.focusIndex = this.elements.length-1;
        }
      }
      this.elements.get(this.focusIndex).select();
    }
    else if($event.key === 'Escape'){
      $event.preventDefault();
      $event.stopPropagation();
      this.reset();
      this.focus($event, false);
      if($event.target){
        (<any>($event.target)).blur();
      }
    }
  }

  private reset(){
    this.form.get('search').setValue(null);
    this.res = [];
    this.focused = true;
  }

  private lastval: string = null;
  keychange($event: KeyboardEvent) {
    const curval = this.form.get('search').value;
    if(this.target && this.lastval !== curval){
      this.loading.next(true);
      this.focusIndex = -1;
      this.lastval = curval;
      return this.keysearch();
    }
  }

  put(event: Event, item: T = null) {
    if(!item && this.elements && this.elements.length && this.focusIndex >= 0 && this.focusIndex < this.elements.length){
      const item = this.elements.get(this.focusIndex);
      if(item && item.value) {
        return this.put(event, item.value);
      }
    }
    else if(!item && this.saveunknown){
      return this.putUnknown(event);
    }

    event.stopPropagation();
    event.preventDefault();

    item = item ?? (this.res.length ? this.res[0] : null);
    if(item) {
      if (!this.isSelected(item)) {
        if (this.single) {
          // Restore the previous selection back into the results list so
          // the sequence "pick A, pick B" leaves A re-pickable (otherwise
          // both would be spliced and A would only come back via a full
          // re-fetch). Guard on prev being an object with an id: a parent
          // form may writeValue() a primitive id, in which case `.id` is
          // undefined and the find() comparison degenerates.
          const prev = this.selected as any;
          if (prev && typeof prev === 'object' && 'id' in prev
              && !this.res.find(o => o.id === prev.id)) {
            this.res.unshift(prev);
          }
          this.selected = item;
        } else {
          this.selections.unshift(item);
        }
        const idx = this.res.findIndex(o => o.id === item.id);
        if (idx !== -1) {
          this.res.splice(idx, 1);
        }

        this.focusIndex = -1;
        if (this.elements) {
          this.elements.forEach(o => o.deselect());
        }

        if (this.flag) {
          this.flag = !this.flag;
        }

        if (this.single) {
          this.onChange(this.selected);
          this.focused = false;
          this.form.get('search').setValue(null);
        } else {
          if(this.textval) {
            this.reset();
          }
          this.onChange(this.selections);
        }
      } else {
        this.delete(item);
      }
    }
    return this.single ? this.selected : this.selections;
  }

  delete(res: T) {
    if(this.single){
      if(this.selected === res){
        this.selected = null;
        this.onChange(this.selected);
        return this.selected;
      }
    }
    else {
      const index = this.selections.findIndex(o => o.id === res.id);
      if (index !== -1) {
        this.selections.splice(index, 1);
        this.onChange(this.selections);
      }
      return this.selections;
    }
  }

  private putUnknown(event: Event){
    const val = this.form.get('search').value;
    if(val && val.trim().length) {
      if (this.res && this.res.length && this.single) {
        const already: T = this.res.find((o) => o[this.title_key]?.toLowerCase() === val.toLowerCase());
        if (already) {
          this.put(event, already);
          return already;
        }
      }

      // get min
      const minid: number = this.selections.reduce((p, c, i) => {
        return c.id < p ? c.id : p;
      }, 0);

      const item: any = {
        id: minid-1
      };
      item[this.title_key] = val.trim().toString();
      this.put(event, <T>item);
      return item;
    }
    return null;
  }

  focus(event: Event, is: boolean) {
    if(!this.disabled) {
      this.focused = is;
      this.onTouched();
      if (!this.focused) {
        if (!this.selected && !this.selections?.length && this.saveunknown && this.curlength) {
          this.putUnknown(event);
        }
        this.res = [];
        this.form.get('search').setValue(null);
      } else if (this.autolist) {
        this.keysearch();
      }
    }
  }

  get curlength(){
    return this.form.get('search').value ? this.form.get('search').value.length : 0;
  }

  addEntry($event: MouseEvent) {
    if(this.addnew) {
      this.adding = true;
      this.addnew($event).then((result: T) => {
        if(result){
          this.put($event, result);
        }
        this.adding = false;
      }).catch(error => {
        this.adding = false;
        console.error(error);
      });
    }
  }
}
